import { ChangeEvent, useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z, ZodIssue } from "zod";
import $api from "@/zapi-frontend/shared/api/axios";
import { registerErrorMessage } from "../const/const";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  getRoutePrivacyNotice,
  getRouteProjects,
  getRouteSignIn,
  getRouteTermsOfUse
} from "@/zapi-frontend/shared/const/router";
import { Input, Button } from "@libs/zapi-ui/shadcn";
import { useHistoricalNavigate } from "@/zapi-frontend/shared/hooks/useHistoricalNavigate";
import { CheckIcon, WarningIcon, LogoSvg } from "@libs/zapi-ui/assets";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useVerifiedToken } from "@/zapi-frontend/pages/SignUpPage/model/useVerifiedToken";
import { useAuthenticated } from "@/zapi-frontend/shared/hooks/useAuthenticated";
import { useCookieConsentStore } from "@/zapi-frontend/shared/hooks/useCookieConsent";

const PasswordSchema = z.string().min(8).max(32).regex(/[A-Z]/).regex(/[0-9]/);
const CredentialsSchema = z.object({
  firstName: z.string().min(2).or(z.literal("")),
  lastName: z.string().min(2).or(z.literal("")),
  email: z.string().email().or(z.literal("")),
  password: PasswordSchema,
  password2: PasswordSchema,
  organizationName: z.string().min(2).or(z.literal("")).nullish()
});

type TCredentials = z.infer<typeof CredentialsSchema>;

export function SignUpPage() {
  const { navigate } = useHistoricalNavigate();
  const [credentials, setCredentials] = useState<TCredentials>({
    firstName: "",
    password: "",
    lastName: "",
    email: "",
    password2: "",
    organizationName: ""
  });
  const [isErrorShown, setIsErrorShown] = useState(false);
  const [issues, setIssues] = useState<ZodIssue[]>([]);
  const [isPasswordTooLong, setIsPasswordTooLong] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);

  const { search } = useLocation();
  const { refetch } = useAuthenticated();
  const { openModal, hasGivenConsent, setUserId } = useCookieConsentStore();

  const signUpToken = new URLSearchParams(search).get("signUpToken");

  const { data } = useVerifiedToken(signUpToken);

  useEffect(() => {
    if (data?.email) setCredentials((prev) => ({ ...prev, email: data.email }));
    if (data?.organizationName) setCredentials((prev) => ({ ...prev, organizationName: data.organizationName }));
  }, [data]);

  const isPasswordLengthValid = credentials.password.length >= 8;
  const isPasswordHasOneNumber = credentials.password.match(/[0-9]/) !== null;
  const isPasswordHasOneCapitalLetter = credentials.password.match(/[A-Z]/) !== null;
  const isPasswordMatching = credentials.password && credentials.password === credentials.password2;

  const isValidCredentials =
    CredentialsSchema.safeParse(credentials).success && isPasswordMatching && !isPasswordTooLong;

  const mutation = useMutation({
    mutationFn: async (credentials: TCredentials) => {
      const payload = {
        email: credentials.email,
        password: credentials.password,
        token: signUpToken,
        firstName: credentials.firstName,
        lastName: credentials.lastName,
        organizationName: !data?.organizationName?.length ? credentials.organizationName : undefined
      };
      return await $api.post("/auth/register", payload);
    },
    onError: (err: AxiosError<{ message: string }>) => {
      const message = err?.response?.data?.message ?? "Failed to sign up";
      toast.error(message);
      setIsErrorShown(true);
    }
  });

  async function getLoginAccess() {
    if (!isValidCredentials) return;
    try {
      await mutation.mutateAsync(credentials);
      const result = await refetch();
      const user = result.data;
      if (user?.id) setUserId(user.id);
      if (!hasGivenConsent()) openModal();
      navigate(getRouteProjects());
    } catch {
      return;
    }
  }

  function onChangeInput(e: ChangeEvent<HTMLInputElement>, name: string) {
    // calculateIssues();
    setIsErrorShown(false);
    if (name === "password") setIsPasswordTooLong(false);

    setCredentials((prev) => ({ ...prev, [name]: e.target.value }));
  }

  function getOnChangeInput(name: string) {
    return (e: ChangeEvent<HTMLInputElement>) => {
      onChangeInput(e, name);
    };
  }

  function onPasswordBlur() {
    if (credentials.password.length > 32) setIsPasswordTooLong(true);
  }

  function calculateIssues() {
    const issues = CredentialsSchema.safeParse(credentials).error?.issues;
    setIssues(issues || []);
  }

  function getIssueMsg(name: string) {
    return issues.find((issue) => issue.path[0] === name)?.message;
  }

  function fInput({
    name,
    keyName,
    showErrors,
    type = "text",
    setShowPassword: setShowPasswordProp,
    showPassword: showPasswordProp,
    disabled,
    onBlur
  }: {
    type?: "password" | "text";
    name: string;
    keyName: keyof TCredentials;
    showErrors?: boolean;
    showPassword?: boolean;
    setShowPassword?: (show: boolean) => void;
    disabled?: boolean;
    onBlur?: () => void;
  }) {
    return (
      <div className={"flex flex-col gap-[4px] flex-grow"}>
        <label className={"text-inLabel font-medium mb-[4px]"}>{name}</label>

        <div className={"relative"}>
          <Input
            type={setShowPasswordProp ? (showPasswordProp ? "text" : "password") : type}
            value={credentials[keyName] ?? ""}
            onChange={getOnChangeInput(keyName)}
            onBlur={onBlur || calculateIssues}
            required
            disabled={disabled}
          />
          {setShowPasswordProp && (
            <button
              type="button"
              onClick={() => setShowPasswordProp(!showPasswordProp)}
              aria-label={showPasswordProp ? "Hide password" : "Show password"}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm font-medium"
            >
              {showPasswordProp ? "Hide" : "Show"}
            </button>
          )}
        </div>
        <p className={"text-errorRed text-xs flex items-center gap-1"}>
          {showErrors && getIssueMsg(keyName) && <WarningIcon width={18} height={18} />}
          {showErrors && getIssueMsg(keyName)}
        </p>
      </div>
    );
  }

  return (
    <div className={"flex flex-col items-center h-[calc(100vh - 40px)]"}>
      <LogoSvg className={"my-[64px]"} />
      <div className={"max-w-[400px] w-full"}>
        <div>
          <div className={"flex mb-10 flex-col items-center"}>
            <h5 className={"text-[32px]"}>Welcome to innovera</h5>
            <p className={"flex gap-1 text-center text-sm mt-[2px] text-innoveraGrayscale-600"}>
              Already have an account?
              <Link to={getRouteSignIn()} className={"text-innoveraOrange-400"}>
                Log in
              </Link>
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              getLoginAccess();
            }}
          >
            <div className={"flex flex-col gap-[16px] mb-6"}>
              <div className={"flex gap-2"}>
                {fInput({
                  name: "First Name",
                  keyName: "firstName",
                  showErrors: true
                })}

                {fInput({
                  name: "Last Name",
                  keyName: "lastName",
                  showErrors: true
                })}
              </div>

              {fInput({
                name: "Email",
                keyName: "email",
                showErrors: true,
                disabled: !!data?.email?.length
              })}

              {fInput({
                name: "Organization name",
                keyName: "organizationName",
                showErrors: true,
                disabled: !!data?.organizationName?.length
              })}

              <div>
                {fInput({
                  name: "Password",
                  keyName: "password",
                  showErrors: false,
                  type: "password",
                  showPassword: showPassword,
                  setShowPassword: setShowPassword,
                  onBlur: onPasswordBlur
                })}

                {isPasswordTooLong && (
                  <p className={"text-errorRed text-xs flex items-center gap-1"}>
                    <WarningIcon width={18} height={18} />
                    Password is too long (maximum 32 characters)
                  </p>
                )}
              </div>

              {fInput({
                name: "Confirm Password",
                keyName: "password2",
                showErrors: false,
                type: "password",
                showPassword: showPassword2,
                setShowPassword: setShowPassword2
              })}

              <div className={"flex gap-[8px] flex-col"}>
                <div className={"flex gap-[8px] items-center"}>
                  <span
                    className={cn([
                      "w-[14px] h-[14px] rounded-full flex items-center justify-center",
                      isPasswordLengthValid ? "text-white bg-successGreen" : "bg-innoveraGrayscale-200"
                    ])}
                  >
                    {isPasswordLengthValid && <CheckIcon width={8} height={8} />}
                  </span>
                  <p
                    className={cn([
                      "text-xs ",
                      isPasswordLengthValid ? "text-successGreen" : "text-innoveraGrayscale-500"
                    ])}
                  >
                    At least 8 characters
                  </p>
                </div>

                <div className={"flex gap-2 items-center"}>
                  <span
                    className={cn([
                      "w-[14px] h-[14px] rounded-full flex items-center justify-center",
                      isPasswordHasOneNumber ? "text-white bg-successGreen" : "bg-innoveraGrayscale-200"
                    ])}
                  >
                    {isPasswordHasOneNumber && <CheckIcon width={8} height={8} />}
                  </span>
                  <p
                    className={cn([
                      "text-xs ",
                      isPasswordHasOneNumber ? "text-successGreen" : "text-innoveraGrayscale-500"
                    ])}
                  >
                    At least one number
                  </p>
                </div>
                <div className={"flex gap-2 items-center"}>
                  <span
                    className={cn([
                      "w-[14px] h-[14px] rounded-full flex items-center justify-center",
                      isPasswordHasOneCapitalLetter ? "text-white bg-successGreen" : "bg-innoveraGrayscale-200"
                    ])}
                  >
                    {isPasswordHasOneCapitalLetter && <CheckIcon width={8} height={8} />}
                  </span>
                  <p
                    className={cn([
                      "text-xs ",
                      isPasswordHasOneCapitalLetter ? "text-successGreen" : "text-innoveraGrayscale-500"
                    ])}
                  >
                    At least one capital letter
                  </p>
                </div>

                <div className={"flex gap-2 items-center"}>
                  <span
                    className={cn([
                      "w-[14px] h-[14px] rounded-full flex items-center justify-center",
                      isPasswordMatching ? "text-white bg-successGreen" : "bg-innoveraGrayscale-200"
                    ])}
                  >
                    {isPasswordMatching && <CheckIcon width={8} height={8} />}
                  </span>
                  <p
                    className={cn([
                      "text-xs ",
                      isPasswordMatching ? "text-successGreen" : "text-innoveraGrayscale-500"
                    ])}
                  >
                    Passwords match
                  </p>
                </div>
              </div>
            </div>
            <Button
              variant="innoveraPrimary"
              className={"w-full"}
              disabled={!isValidCredentials || mutation.isPending}
              type="submit"
            >
              Sign Up
            </Button>

            <p className={"text-errorRed text-xs pt-4"}>{isErrorShown && registerErrorMessage}</p>

            <p className={"text-innoveraGrayscale-600 text-xs pt-4 leading-[18px]"}>
              By clicking "Sign Up" above, you acknowledge that you have read, understood, and agreed to Innovera’s
              <Link to={getRouteTermsOfUse()}>
                {" "}
                <span className={"underline"}>Terms & Conditions</span>
              </Link>{" "}
              and{" "}
              <Link to={getRoutePrivacyNotice()}>
                {" "}
                <span className={"underline"}>Privacy notice</span>
              </Link>{" "}
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
